import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSourceChannels, getSourceChannelsQueryKey, SourceChannelsResponse } from 'api/markerTypes';
import {
  BulkNormalizationOverride,
  generateBulkNormalizationOverrides,
  SlideChannelNormalizationResultSummary,
} from 'api/slideMultiplexChannelNormalizations';
import { buildMarkerNormalizationInsights } from 'components/StudyDashboard/QCMarkers/normalizationInsights';
import { ExperimentResult } from 'interfaces/experimentResults';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { compact, every, filter, includes, intersection, join, map, min, reduce, size, sortBy, uniq } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { getPanelChannelDisplayName, getPanelChannelModalityLabels } from 'utils/multiplex/panelChannelPresentation';
import { CasesParams } from 'utils/useCasesParams/types';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import {
  getConfigurableMarkerIds,
  getCurrentMarkerRangeDetails,
  getDefaultRangesByMarkerId,
  getMissingMarkerNameChannels,
  hasCompleteMarkerNormalization,
  NormalizationRange,
} from './bulkMultiplexNormalizationForm';
import { NormalizationRangeDistribution } from './NormalizationRangeDistribution';
import { PanelNormalizationActions } from './PanelNormalizationActions';
interface PanelNormalizationFormProps {
  casesParams: CasesParams;
  onClose: () => void;
  panel: MultiplexPanelView;
  enabled: boolean;
  disabled?: boolean;
  normalizationSummaries: SlideChannelNormalizationResultSummary[];
  isLoadingNormalization: boolean;
  normalizationAvailable?: boolean;
  canOpenMarkerNamingDialog: boolean;
  openMarkerNamingDialog: (params: { slideId?: string }) => void;
  // Study workflow opens this for every multiplex slide in the study. Skip dataset selection resolution.
  queryStudyMultiplexSlides?: boolean;
}
type SupportedBitDepth = 8 | 16;
export interface PanelNormalizationControls {
  pendingCount: number;
  canSave: boolean;
  isSaving: boolean;
  save: () => Promise<boolean>;
  discard: () => void;
}
interface StainRangeLimit {
  bitDepths: SupportedBitDepth[];
  maximumValue?: number;
}
const getStainRangeLimit = ({
  stainId,
  markerCoverage,
}: {
  stainId: string;
  markerCoverage?: SourceChannelsResponse['markerCoverage'];
}): StainRangeLimit => {
  const bitDepths = sortBy(uniq(compact(markerCoverage?.[stainId]?.bitDepths))) as SupportedBitDepth[];
  const minimumBitDepth = min(bitDepths);
  return {
    bitDepths,
    maximumValue: minimumBitDepth ? 2 ** minimumBitDepth - 1 : undefined,
  };
};
const getStainRangeHelperText = ({
  bitDepths,
  maximumValue,
  currentRangeStatus,
}: StainRangeLimit & {
  currentRangeStatus: 'none' | 'mixed' | 'consistent';
}) => {
  if (maximumValue === undefined) {
    return 'Encoding unknown. Range editing unavailable.';
  }
  const bitDepthLabel = join(
    map(bitDepths, (bitDepth) => `${bitDepth}-bit`),
    ' / '
  );
  const bitDepthGuidance = `${bitDepthLabel} · Whole numbers: 0–${maximumValue}.`;
  if (currentRangeStatus === 'consistent') {
    return bitDepthGuidance;
  }
  if (currentRangeStatus === 'mixed') {
    return `Ranges vary. ${bitDepthGuidance}`;
  }
  return `No saved range. Default: 0–${maximumValue} (${bitDepthLabel}).`;
};
const isValidRange = (minimumValue: number | null, maximumValue: number | null, rangeLimit?: StainRangeLimit) =>
  Number.isInteger(minimumValue) &&
  Number.isInteger(maximumValue) &&
  minimumValue! >= 0 &&
  minimumValue! < maximumValue! &&
  rangeLimit?.maximumValue !== undefined &&
  maximumValue! <= rangeLimit.maximumValue;
const getRangeValidationMessage = (
  minimumValue: number | null,
  maximumValue: number | null,
  rangeLimit?: StainRangeLimit
) => {
  if (rangeLimit?.maximumValue === undefined) {
    return 'Bit-depth metadata is unavailable for this marker.';
  }
  if (!Number.isFinite(minimumValue) || !Number.isFinite(maximumValue)) {
    return 'Enter both a minimum and maximum value.';
  }
  if (!Number.isInteger(minimumValue) || !Number.isInteger(maximumValue)) {
    return 'Enter whole-number values.';
  }
  if (
    minimumValue! < 0 ||
    maximumValue! < 0 ||
    minimumValue! > rangeLimit.maximumValue ||
    maximumValue! > rangeLimit.maximumValue
  ) {
    return `Enter values from 0 to ${rangeLimit.maximumValue}.`;
  }
  if (minimumValue! >= maximumValue!) {
    return 'Minimum value must be smaller than maximum value.';
  }
  return undefined;
};
export const PanelNormalizationForm = React.forwardRef<PanelNormalizationControls, PanelNormalizationFormProps>(
  function PanelNormalizationForm(
    {
      casesParams,
      panel,
      enabled,
      disabled = false,
      normalizationSummaries,
      isLoadingNormalization,
      normalizationAvailable = true,
      canOpenMarkerNamingDialog,
      openMarkerNamingDialog,
    },
    ref
  ) {
    const { labId } = useCurrentLabId();
    const {
      stainTypeIdToDisplayName: getStudyDisplayName,
      stainTypeOptions,
      isLoadingStainTypeOptions,
    } = useStainTypeIdToDisplayName(casesParams.studyId || casesParams.filters?.studyId);
    const stainTypeIdToDisplayName = React.useCallback(
      (markerType: string) => getPanelChannelDisplayName(panel, markerType, stainTypeOptions, getStudyDisplayName),
      [panel, stainTypeOptions, getStudyDisplayName]
    );
    const queryClient = useQueryClient();
    const [hasAttemptedConfigure, setHasAttemptedConfigure] = React.useState(false);
    const [selectedStainIds, setSelectedStainIds] = React.useState<string[] | null>(null);
    const [rangesByStainId, setRangesByStainId] = React.useState<Record<string, NormalizationRange> | null>(null);
    const [touchedMarkerIds, setTouchedMarkerIds] = React.useState<string[]>([]);
    const [editingMarkerIds, setEditingMarkerIds] = React.useState<string[]>([]);
    const requestedEditorFocus = React.useRef<string | null>(null);
    const multiplexCasesParams = casesParams;
    const sourceChannelsQuery = useQuery({
      queryKey: getSourceChannelsQueryKey(multiplexCasesParams),
      queryFn: ({ signal }) => getSourceChannels(multiplexCasesParams, signal),
      enabled,
    });
    const sourceChannelsData = sourceChannelsQuery.data;
    const sourceChannels = sourceChannelsData?.channels || [];
    const markerCoverage = sourceChannelsData?.markerCoverage;
    const selectedSlideCount = sourceChannelsData?.totalSlidesCount || 0;
    const markerIds = React.useMemo(
      () => getConfigurableMarkerIds(sourceChannels, stainTypeOptions),
      [sourceChannels, stainTypeOptions]
    );
    const missingMarkerNameChannels = React.useMemo(
      () => getMissingMarkerNameChannels(sourceChannels, stainTypeOptions),
      [sourceChannels, stainTypeOptions]
    );
    const hasMissingPanelMarkerNames = panel.slides.some((slide) =>
      Object.keys(slide.channelMapping).some((channelId) => !slide.channelMarkerTypes[channelId]?.trim())
    );
    const markerNamesRequired =
      hasMissingPanelMarkerNames ||
      missingMarkerNameChannels.length > 0 ||
      (markerIds.length === 0 && sourceChannels.length > 0);
    const defaultRangesByStainId = React.useMemo(() => {
      const defaults = getDefaultRangesByMarkerId(
        normalizationSummaries,
        markerIds,
        reduce(
          markerIds,
          (slideIdsByMarker, markerId) => {
            slideIdsByMarker[markerId] = markerCoverage?.[markerId]?.slideIds || [];
            return slideIdsByMarker;
          },
          {} as Record<string, string[]>
        )
      );
      for (const markerId of markerIds) {
        const current = getCurrentMarkerRangeDetails(normalizationSummaries, markerId);
        const maximumValue = getStainRangeLimit({ stainId: markerId, markerCoverage }).maximumValue;
        if (current.status === 'none' && maximumValue !== undefined) {
          defaults[markerId] = { min: 0, max: maximumValue };
        }
      }
      return defaults;
    }, [markerCoverage, markerIds, normalizationSummaries]);
    const selectedMarkerIds = selectedStainIds ?? [];
    const rangeMarkerIds = touchedMarkerIds;
    const scopeMarkerIds = rangeMarkerIds.length ? rangeMarkerIds : selectedMarkerIds;
    const normalizationInsights = React.useMemo(
      () => buildMarkerNormalizationInsights(normalizationSummaries, markerIds),
      [normalizationSummaries, markerIds]
    );
    const effectiveRangesByStainId = rangesByStainId ?? defaultRangesByStainId;
    const eligibleSlideIds = React.useMemo(() => {
      if (!scopeMarkerIds.length || !markerCoverage) {
        return [];
      }
      const selectedMarkerSlideIds = map(scopeMarkerIds, (markerId) => markerCoverage[markerId]?.slideIds || []);
      return intersection(...selectedMarkerSlideIds);
    }, [markerCoverage, scopeMarkerIds]);
    const skippedSlides = React.useMemo(
      () =>
        filter(sourceChannelsData?.selectedSlides || [], (slide) => !includes(eligibleSlideIds, slide.slideId)).map(
          (slide) => ({
            ...slide,
            missingMarkerLabels: map(
              filter(
                scopeMarkerIds,
                (markerId) => !includes(markerCoverage?.[markerId]?.slideIds || [], slide.slideId)
              ),
              (markerId) => stainTypeIdToDisplayName(markerId) || markerId
            ),
          })
        ),
      [eligibleSlideIds, markerCoverage, scopeMarkerIds, sourceChannelsData?.selectedSlides, stainTypeIdToDisplayName]
    );
    const skippedSlideCount = Math.max(0, selectedSlideCount - eligibleSlideIds.length);
    const rangeLimitsByStainId = React.useMemo(
      () =>
        reduce(
          markerIds,
          (rangeLimits, stainId) => {
            rangeLimits[stainId] = getStainRangeLimit({ stainId, markerCoverage });
            return rangeLimits;
          },
          {} as Record<string, StainRangeLimit>
        ),
      [markerCoverage, markerIds]
    );
    const selectedStainRanges = React.useMemo(
      () =>
        reduce(
          rangeMarkerIds,
          (stainRanges, stainId) => {
            const range = effectiveRangesByStainId[stainId];
            if (range && isValidRange(range.min, range.max, rangeLimitsByStainId[stainId])) {
              stainRanges[stainId] = [range.min, range.max];
            }
            return stainRanges;
          },
          {} as Record<string, [min: number, max: number]>
        ),
      [effectiveRangesByStainId, rangeLimitsByStainId, rangeMarkerIds]
    );
    const updateStainRange = (stainId: string, value: number | null, boundary: keyof NormalizationRange) => {
      setTouchedMarkerIds((current) => uniq([...current, stainId]));
      setRangesByStainId((currentRanges) => ({
        ...(currentRanges ?? defaultRangesByStainId),
        [stainId]: {
          min: (currentRanges ?? defaultRangesByStainId)[stainId]?.min ?? Number.NaN,
          max: (currentRanges ?? defaultRangesByStainId)[stainId]?.max ?? Number.NaN,
          [boundary]: value ?? Number.NaN,
        },
      }));
    };
    const applyRangesMutation = useMutation<ExperimentResult[], Error, BulkNormalizationOverride>({
      mutationFn: generateBulkNormalizationOverrides,
      onSuccess: (results) => {
        if (results.length) {
          setRangesByStainId(null);
          setTouchedMarkerIds([]);
          setEditingMarkerIds([]);
        }
        enqueueSnackbar(
          results.length
            ? `Updated normalization ranges on ${results.length} selected slides`
            : 'No slides were updated. Check the selected markers and panel scope.',
          { variant: results.length ? 'success' : 'info' }
        );
        queryClient.invalidateQueries(['study-marker-normalization-summary']);
        queryClient.invalidateQueries(['bulkMultiplexNormalizationSummary']);
        queryClient.invalidateQueries(['normalizations']);
      },
      onError: () => {
        enqueueSnackbar('Could not confirm the result. Check current ranges before retrying.', { variant: 'error' });
      },
    });
    const isLoadingFormData = isLoadingStainTypeOptions || isLoadingNormalization || sourceChannelsQuery.isLoading;
    const canConfigure =
      normalizationAvailable &&
      rangeMarkerIds.length > 0 &&
      rangeMarkerIds.length === size(selectedStainRanges) &&
      eligibleSlideIds.length > 0 &&
      selectedSlideCount > 0 &&
      !markerNamesRequired &&
      !sourceChannelsQuery.isError &&
      !isLoadingFormData;
    const configureStepFailed = hasAttemptedConfigure && !canConfigure;
    const applyPanelRanges = async () => {
      setHasAttemptedConfigure(true);
      if (!canConfigure || applyRangesMutation.isLoading) return false;
      try {
        const results = await applyRangesMutation.mutateAsync({
          casesParams: { ...multiplexCasesParams, slideIdsToInclude: eligibleSlideIds },
          labId,
          markerRanges: selectedStainRanges,
          name: 'Bulk normalization range',
          description: 'Set stain-specific channel ranges from the Dataset actions menu.',
        });
        return results.length > 0;
      } catch {
        return false;
      }
    };
    React.useImperativeHandle(ref, () => ({
      pendingCount: touchedMarkerIds.length,
      canSave: canConfigure,
      isSaving: applyRangesMutation.isLoading,
      save: applyPanelRanges,
      discard: () => {
        setRangesByStainId(null);
        setTouchedMarkerIds([]);
        setEditingMarkerIds([]);
        setHasAttemptedConfigure(false);
        applyRangesMutation.reset();
      },
    }));
    if (!isLoadingFormData && markerNamesRequired) {
      return (
        <Stack spacing={1}>
          <Alert severity="info">Set marker names before normalizing this panel.</Alert>
          {canOpenMarkerNamingDialog ? (
            <Button
              variant="contained"
              color="secondary"
              sx={{ alignSelf: 'flex-start' }}
              onClick={() =>
                openMarkerNamingDialog(panel.slides.length === 1 ? { slideId: panel.slides[0].slideId } : {})
              }
            >
              Define multiplex panels
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Ask a study editor to define this panel&apos;s marker names.
            </Typography>
          )}
        </Stack>
      );
    }
    const renderSingleStep = (fields: React.ReactNode) => {
      if (isLoadingFormData)
        return (
          <Stack spacing={1}>
            <Skeleton variant="rectangular" height={120} />
            <Typography role="status" color="text.secondary">
              Loading this panel&apos;s channels and current ranges…
            </Typography>
          </Stack>
        );
      return (
        <Stack spacing={2}>
          <PanelNormalizationActions
            disabled={
              disabled || !normalizationAvailable || sourceChannelsQuery.isError || applyRangesMutation.isLoading
            }
            panel={panel}
            casesParams={multiplexCasesParams}
            markerIds={markerIds}
            selectedMarkerIds={selectedStainIds === null ? [] : selectedMarkerIds}
            onClearSelection={() => setSelectedStainIds([])}
            summaries={normalizationSummaries}
            stains={stainTypeOptions}
            getMarkerLabel={stainTypeIdToDisplayName}
            maximumByMarker={reduce(
              markerIds,
              (values, markerId) => ({ ...values, [markerId]: rangeLimitsByStainId[markerId]?.maximumValue }),
              {} as Record<string, number | undefined>
            )}
            pendingMarkerIds={touchedMarkerIds}
            onStageRanges={(markers, minimum, maximum) => {
              setEditingMarkerIds((current) => uniq([...current, ...markers]));
              setTouchedMarkerIds((current) => uniq([...current, ...markers]));
              setRangesByStainId((current) => ({
                ...(current ?? defaultRangesByStainId),
                ...reduce(
                  markers,
                  (ranges, markerId) => ({ ...ranges, [markerId]: { min: minimum, max: maximum } }),
                  {} as Record<string, NormalizationRange>
                ),
              }));
            }}
          />
          {sourceChannelsQuery.isError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={() => sourceChannelsQuery.refetch()}>
                  Retry
                </Button>
              }
            >
              This panel&apos;s marker information could not be loaded. Retry before applying ranges.
            </Alert>
          )}
          {fields}
          {skippedSlideCount > 0 && scopeMarkerIds.length > 0 && (
            <Box component="details">
              <Typography component="summary" variant="body2">
                {skippedSlideCount} {skippedSlideCount === 1 ? 'slide will' : 'slides will'} remain unchanged because
                selected channels are missing.
              </Typography>
              <TableContainer sx={{ maxHeight: 240 }}>
                <Table size="small" aria-label="Skipped slides and reasons">
                  <TableHead>
                    <TableRow>
                      <TableCell>Slide</TableCell>
                      <TableCell>Slide ID</TableCell>
                      <TableCell>Missing selected channels</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {map(skippedSlides, (slide) => (
                      <TableRow key={slide.slideId}>
                        <TableCell>{slide.originalFileName || slide.slideId}</TableCell>
                        <TableCell>{slide.slideId}</TableCell>
                        <TableCell>{join(slide.missingMarkerLabels, ', ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {applyRangesMutation.isError && (
            <Alert severity="error">
              The update could not be confirmed. Your edits are still here; check current ranges before retrying.
            </Alert>
          )}
          {applyRangesMutation.isSuccess && (
            <Alert severity={applyRangesMutation.data?.length ? 'success' : 'info'}>
              {applyRangesMutation.data?.length
                ? `Normalization ranges updated for ${applyRangesMutation.data.length} slides in this panel.`
                : 'No slides were updated. Check the selected channels and panel scope.'}
            </Alert>
          )}
          {rangeMarkerIds.length > 0 && (
            <Typography variant="body2" color="text.secondary" role="status">
              Update {rangeMarkerIds.length} {rangeMarkerIds.length === 1 ? 'channel' : 'channels'} on{' '}
              {eligibleSlideIds.length} {eligibleSlideIds.length === 1 ? 'slide' : 'slides'} in this panel.
            </Typography>
          )}
        </Stack>
      );
    };
    return renderSingleStep(
      <Stack spacing={1}>
        <TableContainer>
          <Table
            size="small"
            aria-label={`${panel.name || panel.panelId.replace('panel-', 'Panel ')} normalization ranges`}
          >
            <TableHead>
              <TableRow>
                <TableCell>Marker</TableCell>
                <TableCell>Current minimum</TableCell>
                <TableCell>Current maximum</TableCell>
                <TableCell>Normalized slides</TableCell>
                <TableCell>
                  {markerIds.length > 1 ? (
                    <Tooltip title="Select all channels">
                      <Checkbox
                        checked={every(markerIds, (markerId) => includes(selectedMarkerIds, markerId))}
                        indeterminate={
                          selectedMarkerIds.length > 0 &&
                          !every(markerIds, (markerId) => includes(selectedMarkerIds, markerId))
                        }
                        inputProps={{
                          'aria-label': `Select all channels in ${
                            panel.name || panel.panelId.replace('panel-', 'Panel ')
                          }`,
                        }}
                        disabled={disabled || applyRangesMutation.isLoading}
                        onChange={(_event, checked) => setSelectedStainIds(checked ? markerIds : [])}
                      />
                    </Tooltip>
                  ) : (
                    'Select'
                  )}
                </TableCell>
                <TableCell>Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {map(markerIds, (stainId) => {
                const stainRange = effectiveRangesByStainId[stainId];
                const stainLabel = stainTypeIdToDisplayName(stainId) || stainId;
                const rangeLimit = rangeLimitsByStainId[stainId];
                const requiredSlideIds = markerCoverage?.[stainId]?.slideIds || [];
                const currentRangeDetails = getCurrentMarkerRangeDetails(
                  normalizationSummaries,
                  stainId,
                  requiredSlideIds
                );
                const normalizedSlideCount = filter(requiredSlideIds, (slideId) =>
                  hasCompleteMarkerNormalization(normalizationSummaries, stainId, [slideId])
                ).length;
                const rangeValidationMessage = getRangeValidationMessage(
                  stainRange?.min ?? null,
                  stainRange?.max ?? null,
                  rangeLimit
                );
                const showRangeValidationError = Boolean(
                  includes(editingMarkerIds, stainId) &&
                    rangeValidationMessage &&
                    (includes(touchedMarkerIds, stainId) || configureStepFailed)
                );
                const rangeFieldHelperText = showRangeValidationError ? rangeValidationMessage : ' ';
                const rangeFieldHelperTextProps = { sx: { minHeight: '2.5em' } };
                return (
                  <TableRow key={stainId}>
                    <TableCell sx={{ minWidth: 180, verticalAlign: 'top' }}>
                      <Typography fontWeight={600}>{stainLabel}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {join(getPanelChannelModalityLabels(panel, stainId, stainTypeOptions), ' / ')} ·{' '}
                        {getPanelChannelModalityLabels(panel, stainId, stainTypeOptions).includes('RNA')
                          ? 'Visualization range'
                          : 'Positivity classification'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {getStainRangeHelperText({
                          ...rangeLimit,
                          currentRangeStatus: currentRangeDetails.status,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell data-testid={`current-minimum-${stainId}`} sx={{ minWidth: 180 }}>
                      {currentRangeDetails.status === 'consistent' ? (
                        <Typography variant="body2">{currentRangeDetails.range?.min}</Typography>
                      ) : currentRangeDetails.status === 'mixed' ? (
                        <NormalizationRangeDistribution
                          observations={normalizationInsights.observationsByMarkerId[stainId] || []}
                          markerLabel={stainLabel}
                          boundary="minimum"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not normalized
                        </Typography>
                      )}
                      {includes(editingMarkerIds, stainId) && (
                        <TextField
                          fullWidth
                          required
                          label={`${stainLabel} minimum value`}
                          inputRef={(input: HTMLInputElement | null) => {
                            if (input && requestedEditorFocus.current === stainId) {
                              requestedEditorFocus.current = null;
                              input.focus();
                            }
                          }}
                          type="number"
                          inputProps={{ min: 0, max: rangeLimit?.maximumValue, step: 1 }}
                          value={Number.isFinite(stainRange?.min) ? stainRange.min : ''}
                          disabled={disabled || rangeLimit?.maximumValue === undefined || applyRangesMutation.isLoading}
                          onChange={(event) =>
                            updateStainRange(
                              stainId,
                              event.target.value === '' ? null : Number(event.target.value),
                              'min'
                            )
                          }
                          error={showRangeValidationError}
                          helperText={rangeFieldHelperText}
                          FormHelperTextProps={rangeFieldHelperTextProps}
                        />
                      )}
                    </TableCell>
                    <TableCell data-testid={`current-maximum-${stainId}`} sx={{ minWidth: 180 }}>
                      {currentRangeDetails.status === 'consistent' ? (
                        <Typography variant="body2">{currentRangeDetails.range?.max}</Typography>
                      ) : currentRangeDetails.status === 'mixed' ? (
                        <NormalizationRangeDistribution
                          observations={normalizationInsights.observationsByMarkerId[stainId] || []}
                          markerLabel={stainLabel}
                          boundary="maximum"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not normalized
                        </Typography>
                      )}
                      {includes(editingMarkerIds, stainId) && (
                        <TextField
                          fullWidth
                          required
                          label={`${stainLabel} maximum value`}
                          type="number"
                          inputProps={{ min: 0, max: rangeLimit?.maximumValue, step: 1 }}
                          value={Number.isFinite(stainRange?.max) ? stainRange.max : ''}
                          disabled={disabled || rangeLimit?.maximumValue === undefined || applyRangesMutation.isLoading}
                          onChange={(event) =>
                            updateStainRange(
                              stainId,
                              event.target.value === '' ? null : Number(event.target.value),
                              'max'
                            )
                          }
                          error={showRangeValidationError}
                          helperText={rangeFieldHelperText}
                          FormHelperTextProps={rangeFieldHelperTextProps}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {normalizedSlideCount} / {requiredSlideIds.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {normalizedSlideCount === requiredSlideIds.length
                          ? 'Complete'
                          : `${requiredSlideIds.length - normalizedSlideCount} missing`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={includes(selectedMarkerIds, stainId)}
                        disabled={disabled || applyRangesMutation.isLoading}
                        inputProps={{ 'aria-label': `Apply ${stainLabel}` }}
                        onChange={(_event, checked) =>
                          setSelectedStainIds((current) =>
                            checked
                              ? uniq([...(current ?? []), stainId])
                              : filter(current ?? [], (markerId) => markerId !== stainId)
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Edit ${stainLabel} range`}>
                        <IconButton
                          color="inherit"
                          size="small"
                          aria-label={`Edit ${stainLabel} range`}
                          disabled={disabled || applyRangesMutation.isLoading}
                          onClick={() => {
                            requestedEditorFocus.current = stainId;
                            setEditingMarkerIds((current) => uniq([...current, stainId]));
                          }}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {configureStepFailed && selectedMarkerIds.length > 0 && !canConfigure ? (
          <Alert severity="warning">Enter a valid range for every selected marker.</Alert>
        ) : null}
      </Stack>
    );
  }
);
